import type { ResultOf } from '@graphql-typed-document-node/core'
import { ChevronBottomOutlineIcon, ChevronTopOutlineIcon } from '@ikyucom/design-system-icons-react'
import type { ChangeEvent, FC, MouseEvent, PropsWithChildren } from 'react'
import { useCallback, useId, useState } from 'react'
import { useCourseMenuNavigation, usePlanItemNavigation } from '~/features/guide/courses/hooks'
import {
  PlanDiscountLabel,
  PlanFreeFlowLabel,
  PlanPrice,
  PlanRequestReservationLabel,
  PlanSubLabels,
} from '~/features/plan'
import { planDetailPagePath } from '~/lib/path/path'
import { planNameInCourse } from '~/lib/plan/name'
import { css, sva } from '~/styled-system/css'
import { center, hstack, vstack } from '~/styled-system/patterns'
import { Button } from '~/ui'
import Image from '../../image/Image'
import { PointText } from '../../plan/PlanDetail'
import type { Plan } from '../../plan/PlanDetail'
import { CourseNoImage } from '../CourseNoImage'
import type { CourseSelectionInfo } from '../CourseSelectionInfo.frag'
import { RESERVE_LABEL } from '../sd/label'
import type { CourseItemProps, PlanItemProps } from '../type'

const courseItemStyles = sva({
  slots: ['root', 'border', 'plans'],
  base: {
    root: vstack.raw({
      gap: '4',
      padding: '4',
      border: 'medium',
      borderRadius: 'md',
      bg: 'white',
      width: 'full',
    }),
    border: {
      borderStyle: 'dashed',
      borderColor: 'gray.300',
      width: 'full',
    },
    plans: vstack.raw({
      gap: '2',
      width: 'full',
      paddingTop: '4',
      borderTop: 'dotted',
    }),
  },
})

export function CourseItem({ course, plans, restaurant }: CourseItemProps) {
  const onClickMenu = useCourseMenuNavigation(course, plans, restaurant)
  const classes = courseItemStyles()

  const numDefaultShownPlans = 2
  const defaultShownPlans = plans.slice(0, numDefaultShownPlans)
  const defaultHiddenPlans = plans.slice(numDefaultShownPlans)

  return (
    <div className={classes.root}>
      <Header course={course} plans={plans} onClickMenu={onClickMenu} />
      <div className={classes.plans}>
        {defaultShownPlans.map((plan) => (
          <PlanItem key={plan.planId} plan={plan} course={course} restaurant={restaurant} />
        ))}
        {defaultHiddenPlans.length > 0 && (
          <ShowMorePlanItems numPlans={plans.length}>
            {defaultHiddenPlans.map((plan) => (
              <PlanItem key={plan.planId} plan={plan} course={course} restaurant={restaurant} />
            ))}
          </ShowMorePlanItems>
        )}
      </div>
    </div>
  )
}

const showMorePlanItemsStyles = sva({
  slots: ['root', 'plans', 'label', 'labelText'],
  base: {
    root: {
      display: 'grid',
      gridTemplateRows: '[0fr]',
      transition: '[grid-template-rows 0.3s ease-out]',
      width: 'full',
      _peerChecked: {
        gridTemplateRows: '[1fr]',
      },
    },
    plans: vstack.raw({
      gap: '2',
      overflowY: 'hidden',
    }),
    label: {
      border: 'medium',
      borderRadius: 'md',
      cursor: 'pointer',
      py: '2',
      w: 'full',
    },
    labelText: center.raw({
      gap: '1',
      color: 'link',
      fontSize: 'sm',
    }),
  },
  variants: {
    state: {
      closed: {
        label: {
          mt: '-2',
        },
      },
    },
  },
})

function ShowMorePlanItems({ children, numPlans }: PropsWithChildren<{ numPlans: number }>) {
  const [open, setOpen] = useState(false)
  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setOpen(e.target.checked)
  }, [])
  const id = useId()

  const classes = showMorePlanItemsStyles(open ? {} : { state: 'closed' })
  return (
    <>
      <input id={id} type="checkbox" className="peer" onChange={onChange} hidden />
      <div className={classes.root}>
        <div className={classes.plans}>{children}</div>
      </div>
      <label htmlFor={id} className={classes.label}>
        {open ? (
          <div className={classes.labelText}>
            閉じる
            <ChevronTopOutlineIcon />
          </div>
        ) : (
          <div className={classes.labelText}>
            全てのプランをみる（{numPlans}）
            <ChevronBottomOutlineIcon />
          </div>
        )}
      </label>
    </>
  )
}

type HeaderProps = {
  course: ResultOf<typeof CourseSelectionInfo>
  plans: Plan[]
  onClickMenu: (e: MouseEvent<HTMLButtonElement>) => void
}

const headerStyles = sva({
  slots: ['root', 'title', 'course', 'menu'],
  base: {
    root: hstack.raw({
      gap: '3',
      alignItems: 'start',
      justify: 'start',
      width: 'full',
    }),
    title: vstack.raw({
      gap: '2',
      alignItems: 'start',
    }),
    course: {
      fontSize: 'xl',
      fontWeight: 'bold',
    },
    menu: {
      fontSize: 'md',
      color: 'link',
    },
  },
})

function Header({ course, onClickMenu }: HeaderProps) {
  const imageWidth = 114
  const imageHeight = 80

  const classes = headerStyles()

  return (
    <div className={classes.root}>
      {course.images.length !== 0 ? (
        <Image
          alt={`${course.name} - ${course.restaurant.name}`}
          src={course.images[0].url}
          width={imageWidth}
          height={imageHeight}
          className="rounded"
        />
      ) : (
        <CourseNoImage width={imageWidth} height={imageHeight} />
      )}
      <div className={classes.title} style={{ minHeight: imageHeight }}>
        <p className={classes.course}>{course.name}</p>
        <button type="button" className={classes.menu} onClick={onClickMenu}>
          メニューをみる
        </button>
      </div>
    </div>
  )
}

const planItemStyles = sva({
  slots: ['root', 'details', 'labels', 'title', 'link', 'pricing'],
  base: {
    root: hstack.raw({
      backgroundColor: 'gray.100',
      padding: '3',
      justify: 'space-between',
      width: 'full',
      _pc: {
        rounded: 'sm',
      },
    }),
    details: vstack.raw({
      gap: '2',
      alignItems: 'start',
      flex: '1',
    }),
    labels: {
      ...hstack.raw({
        gap: '2',
      }),
      _empty: {
        display: 'none',
      },
    },
    title: {
      fontSize: 'md',
      color: 'gray.800',
    },
    link: {
      fontSize: 'sm',
      color: 'link',
    },
    pricing: hstack.raw({
      gap: '3',
      justify: 'center',
    }),
  },
})

export function PlanItem({ plan, restaurant }: PlanItemProps) {
  const classes = planItemStyles()
  return (
    <div className={classes.root}>
      <div className={classes.details}>
        <div className={css({ spaceY: '1' })}>
          <div className={classes.labels}>
            <PlanFreeFlowLabel plan={plan} />
            <PlanRequestReservationLabel plan={plan} />
            <PlanDiscountLabel plan={plan} price={plan} />
          </div>
          <p className={css({ fontSize: 'md', fontWeight: 'bold' })}>{planNameInCourse(plan)}</p>
          <PlanSubLabels plan={plan} className="mb-1" />
        </div>
        <IkyuOnlyPlanDetailLink plan={plan} restaurant={restaurant} />
      </div>
      <div className={classes.pricing}>
        <div className={css({ display: 'flex', flexDirection: 'column', alignItems: 'end' })}>
          <PlanPrice
            listPrice={plan.listPrice}
            bestPrice={plan.bestPrice}
            priceVaried={plan.priceVaried}
            className={css({ fontSize: '3xl' })}
          />
          <PointText price={plan} restaurant={restaurant} />
        </div>
        <ReserveButton plan={plan} restaurant={restaurant} />
      </div>
    </div>
  )
}

type ButtonProps = Pick<PlanItemProps, 'plan' | 'restaurant'>

/**
 * プラン詳細リンクは一休のみ表示する
 */
const IkyuOnlyPlanDetailLink: FC<ButtonProps> =
  import.meta.env.VITE_MODE === 'ppg' ? EmptyPlanDetailLink : PlanDetailLink

function PlanDetailLink(props: ButtonProps) {
  const { onClickDetail } = usePlanItemNavigation({
    planId: props.plan.planId,
    restaurantId: props.restaurant.restaurantId,
  })
  const { link } = planItemStyles()
  return (
    <a
      href={planDetailPagePath(props.restaurant.restaurantId, props.plan.planId)}
      className={link}
      onClick={onClickDetail}
    >
      詳細をみる
    </a>
  )
}

function EmptyPlanDetailLink() {
  return null
}

const ReserveButton: FC<ButtonProps> =
  import.meta.env.VITE_MODE === 'ppg' ? PpgReserveButton : IkyuReserveButton

function IkyuReserveButton(props: ButtonProps) {
  const { onClickPlan } = usePlanItemNavigation({
    planId: props.plan.planId,
    restaurantId: props.restaurant.restaurantId,
  })
  return (
    <Button size="sm" color="secondarySolid" onClick={onClickPlan}>
      {RESERVE_LABEL}
    </Button>
  )
}

function PpgReserveButton(props: ButtonProps) {
  const { onClickDetail } = usePlanItemNavigation({
    planId: props.plan.planId,
    restaurantId: props.restaurant.restaurantId,
  })
  return (
    <Button size="sm" color="secondarySolid" asChild>
      <a
        onClick={onClickDetail}
        href={planDetailPagePath(props.restaurant.restaurantId, props.plan.planId)}
      >
        {RESERVE_LABEL}
      </a>
    </Button>
  )
}
